import { exactInteger } from '@/lib/exact'
import type { Block, Cell, ChipCell, Column, NoteBlock, TableBlock } from '@/kova/spec/types'
import { ApiError } from '@/lib/api/errors'

import type { DataSource, ScreenData, ScreenLoader } from './provenance'
import { readAtStamp } from './provenance'

/**
 * Configuration › SLA targets (`cf-sla`), from
 * `GET /v1/admin/onboarding/sla-targets`.
 *
 * # This is NOT the breach queue, and the distinction has already misled people
 *
 * `onb-sla` returns SUBJECTS — who is late. This returns the CONFIGURATION the
 * queue is computed from. They cannot be one screen, and the backend records
 * why in terms this console should take personally:
 *
 * > *"An empty queue because no promise was ever made and an empty queue
 * > because everything is on time look identical, and they have opposite
 * > remedies. #764 was raised after two independent readers concluded the
 * > screen was already served because a similarly-named read existed
 * > (go-wallet-admin-client#112)."*
 *
 * Two readers of this repo got it wrong. So the screen says which question it
 * answers, in its own words, at the top.
 *
 * # A state with no target is the finding, not a gap in the table
 *
 * The targets are joined onto the `acceptance_state` enum, so a state nobody
 * has set a target for appears **with no target** rather than not appearing:
 *
 * > *"That row is the finding: subjects sitting in it can never breach, and a
 * > screen that omitted it would present silence as compliance."*
 *
 * Those rows are marked as unmeasurable and are NOT rendered as compliant. A
 * green chip on a state that cannot breach would be the screen lying in the
 * most useful-looking way available to it.
 *
 * # Targets are versioned, and the history is the point
 *
 * > *"raising `in_review` from 48 hours to 72 in place would un-breach every
 * > subject currently breaching, improve the metric, and leave no row saying
 * > the promise had moved."*
 *
 * So the live version is named and superseded ones carry the windows they
 * governed (law 6, invariant 9).
 *
 * # Nothing here writes, and there is no route that would
 *
 * Editing a promise is an instruction with its own authorisation question and
 * was scoped out on purpose. There is no control on this screen, and the
 * absence is stated rather than left to be noticed.
 */

export const SLA_TARGETS_PATH = '/onboarding/sla-targets'

const SOURCE = 'postgres · onboarding_sla_targets (GET /v1/admin/onboarding/sla-targets)'
const AUTHORITY = 'operational'

interface Version {
  readonly version: number | null
  /** Hours, or `null` for a version that set no measurable target. */
  readonly targetHours: number | null
  readonly live: boolean
  readonly from: string
  readonly to: string
  readonly createdBy: string
}

interface Target {
  readonly state: string
  readonly blocking: string
  readonly waitingOn: string
  readonly liveTarget: Version | null
  readonly versions: readonly Version[]
  readonly inStateNow: number | null
  readonly breaching: number | null
  /** The backend's own answer, never rederived from `targetHours`. */
  readonly measured: boolean | null
}

function readVersion(raw: unknown): Version | null {
  if (typeof raw !== 'object' || raw === null) return null
  const o = raw as Record<string, unknown>
  return {
    version: int(o['version']),
    // A POINTER on the wire. Absent means the version set no measurable
    // target, which is different from a target of zero hours — and a zero
    // would render as "instant", which is a promise nobody made.
    targetHours: int(o['target_hours']),
    live: o['live'] === true,
    from: str(o['effective_from']),
    to: str(o['effective_to']),
    createdBy: str(o['created_by']),
  }
}

function readTarget(raw: unknown): Target | null {
  if (typeof raw !== 'object' || raw === null) return null
  const o = raw as Record<string, unknown>
  const state = str(o['state'])
  if (state === '') return null
  const rawVersions = Array.isArray(o['versions']) ? o['versions'] : []
  const versions: Version[] = []
  for (const v of rawVersions) {
    const parsed = readVersion(v)
    if (parsed !== null) versions.push(parsed)
  }
  return {
    state,
    blocking: str(o['blocking']),
    waitingOn: str(o['waiting_on']),
    liveTarget: readVersion(o['live_target']),
    versions,
    inStateNow: int(o['in_state_now']),
    breaching: int(o['breaching']),
    measured: typeof o['measured'] === 'boolean' ? o['measured'] : null,
  }
}

const COLS: readonly Column[] = [
  ['State', 'm', 'minmax(170px,1.1fr)'],
  ['Waiting on', 'x', '140px'],
  ['Blocking', 'x', 'minmax(150px,1fr)'],
  ['Target', 'm', '150px'],
  ['Live version', 'm', '120px'],
  ['In this state now', 'n', '150px'],
  ['Breaching', 'n', '120px'],
  ['Measured', 'c', '190px'],
]

function row(t: Target): readonly Cell[] {
  return [
    t.state,
    t.waitingOn === '' ? '— not stated' : t.waitingOn,
    t.blocking === '' ? '— not stated' : t.blocking,
    targetCell(t),
    t.liveTarget?.version === null || t.liveTarget === null
      ? '— none live'
      : `v${String(t.liveTarget.version)}`,
    t.inStateNow === null ? '— not stated' : String(t.inStateNow),
    t.breaching === null ? '— not stated' : String(t.breaching),
    measuredCell(t),
  ]
}

function targetCell(t: Target): Cell {
  const hours = t.liveTarget?.targetHours
  if (hours === null || hours === undefined) return 'no target set'
  return `${String(hours)} h`
}

/**
 * Whether this state can breach at all.
 *
 * Tone `a` for an unmeasured state, never `g` and never blank. A state nobody
 * set a target for cannot breach, so it never appears on the queue — and
 * rendering that as anything neutral or positive would present silence as
 * compliance, which is the one reading this screen exists to prevent.
 */
function measuredCell(t: Target): ChipCell {
  if (t.measured === null) return ['— not stated', 'r']
  return t.measured
    ? ['measured', 'g']
    : ['no target — cannot breach', 'a']
}

const WHICH_SCREEN: NoteBlock = {
  t: 'note',
  tone: 'n',
  kicker: 'Which question this answers',
  title: 'This is the configuration, not the queue',
  body: 'SLA breaches lists the subjects who are late. This lists the promises those are measured against — including the states nobody has promised anything about, which never appear on the queue at all. The two screens have similar names and answer opposite questions, and reading one for the other is a mistake this codebase has already made twice.',
  ref: 'SLA breaches is the queue · this is what it is computed from',
}

const NO_WRITE: NoteBlock = {
  t: 'note',
  tone: 'n',
  quiet: true,
      kicker: 'Read only',
  title: 'Targets are not edited from here, and no route would let them be',
  body: 'Changing a promise is an instruction with its own authorisation question, and it was deliberately left out of the read that serves this screen. There is no endpoint, so there is no control — and this is not a permission you can be granted.',
  ref: 'no route amends an SLA target',
}

function unmeasuredNote(states: readonly string[]): NoteBlock {
  return {
    t: 'note',
    tone: 'a',
    kicker: 'Unmeasured',
    title: `${String(states.length)} state(s) have no target, so nobody in them can ever be late`,
    body: `${states.join(', ')} — subjects sitting in these states never appear on the breach queue, whatever happens to them, because there is no promise to compare against. An empty breach queue is therefore not evidence that these are healthy. Setting a target is the remedy, and it is not something this console can do.`,
    ref: 'these rows are the finding, not a gap in the table',
  }
}

function versionsNote(t: Target): NoteBlock {
  const superseded = t.versions.filter((v) => !v.live)
  const lines = superseded.map((v) => {
    const hours = v.targetHours === null ? 'no target' : `${String(v.targetHours)} h`
    const window = `${v.from === '' ? '—' : day(v.from)} → ${v.to === '' ? 'open' : day(v.to)}`
    return `v${v.version === null ? '?' : String(v.version)}: ${hours}, ${window}${v.createdBy === '' ? '' : `, set by ${v.createdBy}`}`
  })
  return {
    t: 'note',
    tone: 'n',
    kicker: `${t.state} · history`,
    title: `${String(superseded.length)} superseded target(s) on ${t.state}`,
    body: `${lines.join(' · ')}. Each is carried with the window it governed, because a target changed in place would un-breach everybody currently breaching, improve the number, and leave nothing saying the promise had moved.`,
    ref: 'targets are versioned · a superseded one still explains a breach recorded under it',
  }
}

export function composeSLATargets(payload: Record<string, unknown>, readAt: Date): ScreenData {
  const raw = Array.isArray(payload['targets']) ? (payload['targets'] as readonly unknown[]) : []
  const rows: Target[] = []
  let unreadable = 0
  for (const entry of raw) {
    const parsed = readTarget(entry)
    if (parsed === null) unreadable += 1
    else rows.push(parsed)
  }

  const table: TableBlock = {
    t: 'table',
    title: 'Onboarding SLA targets',
    src: 'ledger',
    facets: false,
    cols: COLS,
    rows: rows.map(row),
    footer: `${String(rows.length)} state(s)${unreadable === 0 ? '' : ` · ${String(unreadable)} unreadable`}`,
    foot2:
      'Every state in the enum appears, including those with no target — a state that is absent from this table is one this read did not return, not one that is fine.',
  }

  const blocks: Block[] = [WHICH_SCREEN, table]

  const unmeasured = rows.filter((t) => t.measured === false).map((t) => t.state)
  if (unmeasured.length > 0) blocks.push(unmeasuredNote(unmeasured))

  for (const t of rows) {
    if (t.versions.some((v) => !v.live)) blocks.push(versionsNote(t))
  }

  // The backend's own three notes, rendered rather than paraphrased. Each says
  // something about the limits of the data that this console has no
  // independent way to know.
  for (const [key, kicker] of [
    ['unmeasured_note', 'From the backend'],
    ['p95_note', 'What cannot be served'],
    ['write_note', 'From the backend'],
  ] as const) {
    const body = str(payload[key])
    if (body !== '') {
      blocks.push({
        t: 'note', tone: 'n', kicker, title: 'The backend’s own words on this read', body,
        ref: key,
      })
    }
  }

  blocks.push(NO_WRITE)

  if (unreadable > 0) {
    blocks.push({
      t: 'note',
      tone: 'r',
      kicker: 'Partial read',
      title: `${String(unreadable)} target row(s) could not be read`,
      body: 'They are counted rather than omitted silently. A missing state on this screen reads as "there is no such state", which is the opposite of what an unreadable row means.',
      ref: 'the count is the finding',
    })
  }

  if (rows.length === 0 && unreadable === 0) {
    blocks.push({
      t: 'note',
      tone: 'r',
      kicker: 'SLA targets',
      title: 'No state came back at all',
      body: 'The endpoint answered with an empty list. That is not "no targets are set" — the states come from an enum and are joined to whatever targets exist, so an empty answer means the read itself returned nothing rather than that nothing is promised.',
      ref: 'an empty list here is a read problem, not a configuration one',
    })
  }

  const missing: string[] = []
  if (unreadable > 0) missing.push(`${String(unreadable)} unreadable row(s)`)
  if (rows.some((t) => t.measured === null)) missing.push('whether at least one state is measured')
  // Always. The screen draws no p95 column because nothing can produce one.
  missing.push('p95 actual — no state-transition history exists to compute it from')

  const provenance: DataSource = {
    kind: 'partial', source: SOURCE, authority: AUTHORITY, readAt: readAtStamp(readAt), missing,
  }
  return { blocks, provenance }
}

export const loadSLATargets: ScreenLoader = async (api) => {
  let payload: unknown
  try {
    payload = await api.get<unknown>(SLA_TARGETS_PATH)
  } catch (error) {
    if (error instanceof ApiError) {
      return unreadableScreen(
        `The SLA targets endpoint answered ${String(error.status)}${error.code === null ? '' : ` (${error.code})`}.`,
        error.decisionRef,
      )
    }
    if (error instanceof DOMException && error.name === 'AbortError') throw error
    return unreadableScreen('The backend could not be reached.', null)
  }
  if (typeof payload !== 'object' || payload === null) {
    return unreadableScreen('The SLA targets endpoint returned a shape this console does not know.', null)
  }
  return composeSLATargets(payload as Record<string, unknown>, new Date())
}

function unreadableScreen(reason: string, decisionRef: string | null): ScreenData {
  return {
    blocks: [{
      t: 'note',
      tone: 'r',
      kicker: 'Not read',
      title: 'The SLA targets could not be read',
      body: `${reason} Nothing is drawn from it. In particular this is NOT an empty configuration: whether any promise exists is unknown.`,
      ref: decisionRef === null ? 'no decision reference was supplied' : `decision_ref ${decisionRef}`,
    }],
    provenance: { kind: 'unread', source: SOURCE, reason },
  }
}

function str(v: unknown): string {
  return typeof v === 'string' ? v : ''
}

function int(v: unknown): number | null {
  return exactInteger(v)
}

function day(stamp: string): string {
  const t = stamp.indexOf('T')
  return t === -1 ? stamp : stamp.slice(0, t)
}

// ===========================================================================
// Configuration › Reason-code registry (`cf-reasons`)
// ===========================================================================

/**
 * The one screen where a missing control must be ABSENT rather than disabled.
 *
 * # Why this is not law 18
 *
 * Law 18 says a permission-gated control is shown disabled with the required
 * role named, never hidden, **so staff know an escalation path exists**. That
 * is the whole justification, and it is exactly what fails here.
 *
 * There is no POST, no PUT and no DELETE on reason codes anywhere in the
 * backend, and there will not be one: adding a reason code is a code change
 * with a resolver written beside it, never an editorial act. So there is no
 * role that unlocks it, no request anybody could make, and nobody to make it
 * of. A disabled "Add reason code" button would advertise an escalation path
 * that does not exist — and somebody would follow it, and waste a compliance
 * officer's afternoon discovering that.
 *
 * The rule underneath both is one rule: **show the path when there is one, and
 * say there is none when there is not.** A disabled control is the first; this
 * note is the second.
 *
 * # Why the screen exists at all rather than being dropped from the rail
 *
 * Because the question is real. An operator picking a reason code on any of the
 * thirty-odd controls this console now has will eventually want to know where
 * the list comes from and how to change it. Answering that with a missing menu
 * entry teaches them nothing; answering it with this teaches them the thing
 * they actually need, which is that the answer is a pull request.
 */
export const loadReasonRegistry: ScreenLoader = () => Promise.resolve({
  blocks: [{
    t: 'note',
    tone: 'n',
    quiet: true,
      kicker: 'By design',
    title: 'Reason codes are not edited here, or anywhere, by anybody',
    body: 'Every reason code this console offers is part of the software. There is no endpoint that adds, renames or retires one, no permission that would unlock such a thing, and no plan to build one — which is why there is no disabled control on this screen for you to ask about. A reason code is not a label: something reads it and decides differently because of it, so adding one is a change to the code with the rule that consumes it written beside it, reviewed and deployed together. Needing a code that does not exist is a request to engineering, not an editorial task, and the honest thing this screen can do is say so rather than draw a button nobody could ever enable.',
    ref: 'no POST, PUT or DELETE on reason codes exists · the registry ships with the build',
  }],
  provenance: {
    kind: 'not_built',
    screen: 'cf-reasons',
    reason: 'There is no endpoint. Nothing was read because there is nothing to read — this is a statement about the design rather than a failed request.',
  },
})
